import {
    useGetRsvpRequestsQuery,
    useRespondToRsvpMutation,
} from '../../../../app/Slices/SevenDayApiSlice';
import { Check, Clear } from '@mui/icons-material';
import {
    Box,
    ButtonGroup,
    Grid,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Typography,
} from '@mui/material';
import React, { MouseEvent } from 'react';
import toast from '../../../shared/Toast';
import { useNavigate } from 'react-router-dom';
import { CustomButton } from '../../../../layout/styledcomponents/CustomButton';
import moment from 'moment';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { CustomIconButton } from '../../../../layout/styledcomponents/CustomIconButton';
import { RsvpRequest, RsvpStatus } from '../../../types/reservation';

const formatRsvpDate = (startDate: string) =>
    moment(new Date(startDate)).format('ll');

const formatRsvpTimeRange = (request: RsvpRequest) => {
    const bookingInfo = request.bookingInfo as {
        checkinTime?: string;
        checkoutTime?: string;
    };

    if (bookingInfo?.checkinTime && bookingInfo?.checkoutTime) {
        const start = moment(bookingInfo.checkinTime, ['H:mm', 'HH:mm'], true);
        const end = moment(bookingInfo.checkoutTime, ['H:mm', 'HH:mm'], true);
        if (start.isValid() && end.isValid()) {
            return `${start.format('h:mm A')} to ${end.format('h:mm A')}`;
        }
    }

    const start = moment(request.startDate);
    const end = moment(request.endDate);
    if (start.isValid() && end.isValid()) {
        return `${start.format('h:mm A')} to ${end.format('h:mm A')}`;
    }

    return '-';
};

const formatBookerName = (booker: RsvpRequest['booker']) =>
    [booker?.firstName, booker?.lastName].filter(Boolean).join(' ') || '-';

const RsvpRequests = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const navigate = useNavigate();

    const { data: rsvps } = useGetRsvpRequestsQuery({});

    const [respondToRsvp] = useRespondToRsvpMutation();

    const handleRespond = async (
        e: MouseEvent<HTMLButtonElement>,
        bookingId: number,
        rsvpId: string,
        status: RsvpStatus,
    ) => {
        try {
            e.stopPropagation();
            await respondToRsvp({
                bookingId,
                rsvpId,
                status,
            }).unwrap();

            toast.success(`RSVP request ${status} successfully.`);

            if (status === 'accepted') {
                navigate('/reservations?tab=3');
            }
        } catch {
            toast.error('Error responding to RSVP');
        }
    };

    return (
        <div>
            <Grid
                sx={{
                    width: '100%',
                    justifyContent: 'left',
                    display: 'flex',
                }}
            >
                <Typography
                    variant='h1'
                    sx={{
                        textAlign: 'left',
                        textTransform: 'uppercase',
                    }}
                >
                    RSVP Requests
                </Typography>
            </Grid>
            {rsvps && rsvps.length > 0 ? (
                <Grid
                    container
                    spacing={2}
                    sx={{
                        width: '100%',
                        alignItems: 'flex-start',
                        mb: 2,
                    }}
                >
                    <Grid item xs={12} lg={11.4}>
                        <List disablePadding>
                            {rsvps.map((request: RsvpRequest) => (
                                <ListItem
                                    key={request.rsvp.id}
                                    divider
                                    disableGutters
                                    onClick={() =>
                                        navigate(
                                            `/reservations/${request.module.name}/${request.id}`,
                                        )
                                    }
                                    sx={{ px: 0 }}
                                >
                                    <ListItemButton
                                        sx={{
                                            px: 0,
                                            py: 1.5,
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                            gap: 2,
                                        }}
                                    >
                                        <ListItemText
                                            primary={
                                                <Typography
                                                    component='div'
                                                    variant='body1'
                                                    sx={{ fontWeight: 600 }}
                                                >
                                                    Reservation #{request.id}{' '}
                                                    for {request.module.name} -
                                                    hosted by{' '}
                                                    {request.owner.name}
                                                </Typography>
                                            }
                                            secondary={
                                                <Box
                                                    component='div'
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 0.25,
                                                        mt: 0.5,
                                                    }}
                                                >
                                                    <Typography
                                                        variant='body2'
                                                        color='text.secondary'
                                                    >
                                                        Date:{' '}
                                                        {formatRsvpDate(
                                                            request.startDate,
                                                        )}
                                                    </Typography>
                                                    <Typography
                                                        variant='body2'
                                                        color='text.secondary'
                                                    >
                                                        Time:{' '}
                                                        {formatRsvpTimeRange(
                                                            request,
                                                        )}
                                                    </Typography>
                                                    <Typography
                                                        variant='body2'
                                                        color='text.secondary'
                                                    >
                                                        Primary Contact:{' '}
                                                        {formatBookerName(
                                                            request.booker,
                                                        )}{' '}
                                                        - {request.guest.name}
                                                    </Typography>
                                                </Box>
                                            }
                                            primaryTypographyProps={{
                                                component: 'div',
                                            }}
                                            secondaryTypographyProps={{
                                                component: 'div',
                                            }}
                                        />
                                        <ButtonGroup>
                                            {isMobile ? (
                                                <>
                                                    <CustomIconButton
                                                        color='primary'
                                                        onClick={(
                                                            e: MouseEvent<HTMLButtonElement>,
                                                        ) =>
                                                            handleRespond(
                                                                e,
                                                                request.id,
                                                                request.rsvp.id,
                                                                'accepted',
                                                            )
                                                        }
                                                    >
                                                        <Check />
                                                    </CustomIconButton>
                                                    <CustomIconButton
                                                        color='warning'
                                                        onClick={(
                                                            e: MouseEvent<HTMLButtonElement>,
                                                        ) =>
                                                            handleRespond(
                                                                e,
                                                                request.id,
                                                                request.rsvp.id,
                                                                'declined',
                                                            )
                                                        }
                                                    >
                                                        <Clear />
                                                    </CustomIconButton>
                                                </>
                                            ) : (
                                                <>
                                                    <CustomButton
                                                        variant='outlined'
                                                        startIcon={<Check />}
                                                        color='primary'
                                                        onClick={(
                                                            e: MouseEvent<HTMLButtonElement>,
                                                        ) =>
                                                            handleRespond(
                                                                e,
                                                                request.id,
                                                                request.rsvp.id,
                                                                'accepted',
                                                            )
                                                        }
                                                    >
                                                        Accept
                                                    </CustomButton>
                                                    <CustomButton
                                                        variant='outlined'
                                                        startIcon={<Clear />}
                                                        color='warning'
                                                        onClick={(
                                                            e: MouseEvent<HTMLButtonElement>,
                                                        ) =>
                                                            handleRespond(
                                                                e,
                                                                request.id,
                                                                request.rsvp.id,
                                                                'declined',
                                                            )
                                                        }
                                                    >
                                                        Decline
                                                    </CustomButton>
                                                </>
                                            )}
                                        </ButtonGroup>
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    </Grid>
                </Grid>
            ) : (
                <Typography sx={{ mt: 2 }} color='text.secondary'>
                    You have no pending RSVP requests.
                </Typography>
            )}
        </div>
    );
};

export default RsvpRequests;
